import { Skeleton } from '@mui/material'
import { useAuth } from 'context/AuthContext'
import { loadingContext } from 'context/LoadingContext'
import { reloadTableContext } from 'context/ReloadTableContext'
import { toastContext } from 'context/ToastContext'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { httpService } from 'services/httpService'

const useQuery = ({
  defaultUrl = '',
  defaultEndpoint = '',
  defaultMethod = 'GET',
  defaultBody,
  initialData = null,
  isControlled = false,
  dependencies = [],
  toastOptions: {
    resultMessageSuccess: defaultResultMessageSuccess = '',
    resultMessageError: defaultResultMessageError = '',
    showFeedbackMessage:
      defaultShowFeedbackMessage = defaultMethod.toLowerCase() !== 'get',
    duration: defaultDuration = 5,
  } = {},
  loaderWidth = '100%',
  loaderHeight = '20vh',
  disableAutoLogoutOn401 = false,
  reloadTableOnResolve = false,
  showInitialLoading = false,
  hideGlobalLoading: defaultHideGlobalLoading = false,
  onResolveSuccess = () => {},
  onResolveError = () => {},
  onResolve = () => {},
}) => {
  const { setLoading } = useContext(loadingContext)
  const { setToastOptions } = useContext(toastContext)
  const { reloadTable } = useContext(reloadTableContext)
  const { logout } = useAuth()

  const [data, setData] = useState(initialData)
  const [fetching, setFetching] = useState(!isControlled)
  const aborter = useRef(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedInitialData = useMemo(() => initialData, [])
  const makeRequest = useCallback(
    async ({
      url = defaultUrl,
      method = defaultMethod,
      endpoint = defaultEndpoint,
      body = defaultBody,
      token,
      hideGlobalLoading = defaultHideGlobalLoading,
      withCredentials = true,
      toastSeverity,
      toastOptions: {
        showFeedbackMessage = defaultShowFeedbackMessage,
        resultMessageSuccess = defaultResultMessageSuccess,
        resultMessageError = defaultResultMessageError,
        duration = defaultDuration,
      } = {},
    } = {}) => {
      setFetching(true)
      const newAborter = new AbortController()
      aborter.current = newAborter

      const showGlobalLoading = (!hideGlobalLoading
          && (isControlled || data !== memoizedInitialData))
        || (showInitialLoading && memoizedInitialData === initialData)
      if (showGlobalLoading) setLoading(true)

      const response = await httpService({
        method,
        url,
        endpoint,
        body,
        token,
        aborter: aborter.current,
        setAborter: (newAborter) => (aborter.current = newAborter),
        withCredentials,
      })
      if (showGlobalLoading) setLoading(false)
      setFetching(false)
      if (!response) return // in abort case

      response.message = response.message
        || (response.code === 400
          ? response.data
          : defaultStatusMessages[response.code]
            || defaultStatusMessages[response.error ? 500 : 'success'])

      // eslint-disable-next-line no-console
      console.log({ ...response, endpoint: endpoint || url })
      if (!disableAutoLogoutOn401 && response.code === 401) await logout(true)
      setData(response.data)
      if (reloadTableOnResolve) reloadTable()

      // ----------------------
      if (response.error) {
        onResolveError(response.data)
        if (
          typeof resultMessageError === 'object'
          && resultMessageError[response.code]
        ) {
          response.message = resultMessageError[response.code]
        } else if (
          !!resultMessageError
          && typeof resultMessageError === 'string'
        ) {
          response.message = resultMessageError
        }
      } else {
        onResolveSuccess(response.data)
        if (
          typeof resultMessageSuccess === 'object'
          && resultMessageSuccess[response.code]
        ) {
          response.message = resultMessageSuccess[response.code]
        } else if (
          !!resultMessageSuccess
          && typeof resultMessageSuccess === 'string'
        ) {
          response.message = resultMessageSuccess
        }
      }
      if (
        response.error
        || (showFeedbackMessage && (resultMessageSuccess || resultMessageError))
      ) {
        setToastOptions({
          message: response.message,
          error: response.error,
          duration: duration * 1000,
          severity: response.error ? 'error' : toastSeverity,
        })
      }
      // ----------------------

      onResolve(response.data)
      return response
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      memoizedInitialData,
      defaultEndpoint,
      defaultMethod,
      defaultBody,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ...dependencies,
    ],
  )

  useEffect(() => {
    if (!isControlled) makeRequest()
  }, [makeRequest, isControlled])

  useEffect(() => {
    return () => {
      if (aborter.current) aborter.current.abort('aborted')
    }
  }, [])

  return {
    data,
    makeRequest,
    setData,
    fallbackComponent: fetching ? (
      <Skeleton variant='rounded' width={loaderWidth} height={loaderHeight} />
    ) : null,
  }
}

export const defaultStatusMessages = {
  success: 'Operación exitosa',
  400: 'Operación inválida',
  401: 'Usuario o contraseña incorrectos',
  403: 'No autorizado',
  409: 'Ya existe un usuario con ese mail',
  500: 'Ha ocurrido un error',
}

export default useQuery
// eslint-disable-next-line no-promise-executor-return
export const timeout = (delay) => new Promise((res) => setTimeout(res, delay))
